import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Search, Ban, Trash2, UserX } from "lucide-react";
import { Input } from "@/components/ui/input";
import { AdminTitle, DataTable } from "@/pages/admin/AdminLayout";
import { Badge } from "@/pages/Account";
import { Spinner, Empty } from "@/components/site/Bits";
import { useApi, useDownload, fmtDateTime, fmtPrice, errMsg } from "@/lib/api";

export default function Students() {
  const api = useApi();
  const download = useDownload();
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => api("get", "/admin/students", null, { params: { q } }).then(setList), 300);
    return () => clearTimeout(t);
  }, [api, q, tick]);
  const run = async (fn, ok) => {
    try { await fn(); toast.success(ok); setTick((x) => x + 1); } catch (err) { toast.error(errMsg(err)); }
  };
  const cancelEnr = (e) => {
    const reason = window.prompt(`إلغاء تسجيل ${e.reg_no} في "${e.course_title}"؟\nاكتب السبب (اختياري) — سيصل للمتدرب بريد بالإلغاء:`, "");
    if (reason === null) return;
    run(() => api("post", `/admin/enrollments/${e.id}/cancel`, { reason, notify: true }), "تم إلغاء التسجيل وتحرير المقعد");
  };
  const deleteEnr = (e) => {
    if (!window.confirm(`حذف المتدرب من دورة "${e.course_title}" نهائياً؟ سيُحذف سجل التسجيل (تبقى المدفوعات الناجحة في السجل المالي).`)) return;
    run(() => api("delete", `/admin/enrollments/${e.id}`), "تم حذف المتدرب من الدورة");
  };
  const deleteUser = (s) => {
    if (!window.confirm(`حذف العضو "${s.full_name}" نهائياً؟ سيُحذف حسابه وجميع تسجيلاته وتُحرَّر مقاعده.`)) return;
    run(() => api("delete", `/admin/users/${s.user_id}`), "تم حذف العضو");
  };
  return (
    <div data-testid="admin-students">
      <AdminTitle title="إدارة المتدربين">
        <button className="btn-gold-outline !py-2.5" onClick={() => download("/admin/students/export", "students.xlsx").catch(() => toast.error("تعذر التصدير"))} data-testid="export-students-btn"><Download size={16} />تصدير Excel</button>
      </AdminTitle>
      <div className="relative max-w-md mb-5">
        <Search size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted-ink)]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالاسم أو الهاتف أو البريد أو رقم التسجيل" className="pr-10 h-11 bg-white" data-testid="students-search-input" />
      </div>
      {!list ? <Spinner /> : !list.length ? <Empty text="لا يوجد متدربون" /> : (
        <DataTable head={["الاسم", "الهاتف", "البريد", "عدد الدورات", "آخر حالة", ""]} testId="students-table">
          {list.map((s) => (
            <Fragment key={s.user_id}>
              <tr data-testid={`student-row-${s.user_id}`}>
                <td className="font-semibold">{s.full_name}</td><td dir="ltr" className="text-right">{s.phone}</td><td dir="ltr" className="text-right">{s.email}</td>
                <td>{s.enrollments.length}</td><td><Badge s={s.enrollments[0]?.status} /></td>
                <td className="whitespace-nowrap"><button className="text-[var(--copper)] text-xs font-semibold" onClick={() => setOpen(open === s.user_id ? null : s.user_id)} data-testid={`student-details-${s.user_id}`}>{open === s.user_id ? "إخفاء" : "التفاصيل"}</button>
                  <button className="text-red-600 text-xs font-semibold mr-3 inline-flex items-center gap-1" onClick={() => deleteUser(s)} data-testid={`student-delete-${s.user_id}`}><UserX size={14} />حذف العضو</button></td>
              </tr>
              {open === s.user_id && (
                <tr className="bg-[var(--paper)]"><td colSpan={6}>
                  <div className="space-y-2" data-testid={`student-enrollments-${s.user_id}`}>
                    {s.enrollments.map((e) => (
                      <div key={e.id} className="flex flex-wrap gap-x-6 gap-y-1 text-xs bg-white rounded-lg p-3 border border-[var(--line)]">
                        <span>رقم التسجيل: <b dir="ltr">{e.reg_no}</b></span><span>الدورة: <b>{e.course_title}</b></span>
                        <span>الحالة: <Badge s={e.status} /></span><span>المبلغ: {fmtPrice(e.amount)}</span>
                        <span>الحضور: {e.attendance === "present" ? "حاضر" : e.attendance === "absent" ? "غائب" : "—"}</span>
                        <span>الشهادة: {e.certificate_no || "—"}</span><span>التاريخ: {fmtDateTime(e.created_at)}</span>
                        <span className="mr-auto flex gap-3">
                          {e.status !== "cancelled" && <button onClick={() => cancelEnr(e)} className="inline-flex items-center gap-1 text-amber-700 font-semibold" data-testid={`enr-cancel-${e.id}`}><Ban size={13} />إلغاء التسجيل</button>}
                          <button onClick={() => deleteEnr(e)} className="inline-flex items-center gap-1 text-red-600 font-semibold" data-testid={`enr-delete-${e.id}`}><Trash2 size={13} />حذف من الدورة</button>
                        </span>
                      </div>
                    ))}
                  </div>
                </td></tr>
              )}
            </Fragment>
          ))}
        </DataTable>
      )}
    </div>
  );
}
